<template>
  <!-- HeroScene — Render Model: background → glow → content -->
  <section class="hero-scene scene" :class="ready ? 'render-ready' : 'render-locked'">

    <!-- Layer 0: Background -->
    <div class="scene-bg"></div>

    <!-- Layer 1: Glow (radial behind coin orbit) -->
    <div class="scene-glow glow-primary" style="top:30%;left:50%;transform:translate(-50%,-50%)"></div>
    <!-- Layer 1b: Directional light gradient (2nd glow) -->
    <div class="scene-glow glow-accent" style="top:45%;left:30%"></div>
    <!-- Layer 1c: Subtle ambient glow (3rd glow) -->
    <div class="scene-glow glow-secondary" style="top:55%;left:65%"></div>
    <!-- Noise overlay -->
    <div class="scene-noise"></div>

    <!-- Ambient decoration dots -->
    <div class="ambient-dots" style="top:20%;right:15%"></div>
    <div class="ambient-dots" style="top:55%;left:10%"></div>

    <!-- Layer 3: Content -->
    <div class="scene-content">
      <!-- Title -->
      <div class="hero-title">
        <h1>Trade like a pro</h1>
      </div>

      <!-- Coin Orbit -->
      <div class="coin-orbit layer-isolate">
        <div class="orbit-ring ring-1"></div>
        <div class="orbit-ring ring-2"></div>
        <div class="orbit-ring ring-3"></div>
        <div class="coin coin-1 micro-pulse"><span>Ξ</span></div>
        <div class="coin coin-2 micro-pulse"><span>K</span></div>
        <div class="coin coin-3 micro-pulse"><span>₿</span></div>
        <div class="coin coin-4 micro-pulse"><span>○</span></div>
        <div class="swipe-arrow"><span class="arrow-icon">→</span></div>
      </div>

      <!-- Ticker Scene -->
      <div class="ticker-scene">
        <div ref="tickerSwiper" class="swiper-container market-swiper">
          <div class="swiper-wrapper">
            <div v-for="coin in coins" :key="coin.id" class="swiper-slide coin-card">
              <div class="coin-info">
                <span class="coin-symbol">{{ coin.fromSymbol }}</span>
                <span class="coin-price number" :class="getChangeClass(coin.change)">${{ coin.price }}</span>
                <span class="coin-change number" :class="getChangeClass(coin.change)">{{ coin.change }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Swiper from 'swiper'
import { fetchHomeData } from '@/api'
import { bootSequence } from '@/sync/boot'
import { initSynced } from '@/sync/swiper-sync'

export default {
  name: 'HeroScene',
  data() { return { coins: [], ready: false, tickerSwiper: null } },
  async mounted() {
    const sync = await bootSequence({
      fetchData,
      extractImages: (d) => [].concat(
        d.banners.map(b => b.url),
        d.partners.map(p => p.url),
      ),
      getSwipers: () => [this.tickerSwiper],
    })
    this.coins = sync.data.coins
    await this.$nextTick()

    this.tickerSwiper = initSynced(Swiper, this.$refs.tickerSwiper, {
      slidesPerView: 'auto',
      centeredSlides: false,
      spaceBetween: 12,
      freeMode: true,
      loop: true,
      autoplay: { delay: 0, disableOnInteraction: false },
      speed: 600,
    })

    this.ready = true
  },
  methods: {
    getChangeClass(change) { return parseFloat(change) >= 0 ? 'up' : 'down' }
  }
}
</script>

<style scoped>
/* ===== HeroScene Layout ===== */
.hero-title { text-align: center; padding: var(--s-5) var(--s-5) 0; }
.hero-title h1 {
  font-size: var(--font-size-3xl); font-weight: 700;
  color: var(--text-primary); margin-bottom: var(--s-6);
}

/* ===== Coin Orbit ===== */
.coin-orbit { position: relative; width: 200px; height: 200px; margin: 0 auto; }
.orbit-ring {
  position: absolute; border: 1px solid rgba(255,255,255,0.08);
  border-radius: var(--radius-round); top: 50%; left: 50%;
  transform: translate(-50%,-50%);
}
.ring-1 { width: 120px; height: 120px; }
.ring-2 { width: 160px; height: 160px; }
.ring-3 { width: 200px; height: 200px; }
.coin {
  position: absolute; width: 36px; height: 36px;
  border-radius: var(--radius-round);
  display: flex; align-items: center; justify-content: center;
  font-size: var(--font-size-md); font-weight: 700;
  color: var(--text-primary);
  animation: float 3s ease-in-out infinite;
}
.coin-1 { background: linear-gradient(135deg, #627eea, #3c5be0); top: 10px; left: 50%; transform: translateX(-50%); animation-delay: 0s; }
.coin-2 { background: linear-gradient(135deg, #f7931a, #c87613); top: 50%; right: 10px; transform: translateY(-50%); animation-delay: .5s; }
.coin-3 { background: linear-gradient(135deg, #345d9d, #264a85); top: 50%; left: 10px; transform: translateY(-50%); animation-delay: 1s; }
.coin-4 { background: linear-gradient(135deg, var(--primary-deep), var(--primary)); top: 60%; left: 50%; transform: translateX(-50%); animation-delay: 1.5s; }
@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
.swipe-arrow {
  position: absolute; bottom: var(--s-5); left: 50%;
  transform: translateX(-50%); width: 50px; height: 50px;
  border: 1px solid rgba(255,255,255,.25);
  border-radius: var(--radius-round);
  display: flex; align-items: center; justify-content: center;
  animation: pulse 2s infinite;
}
.arrow-icon { color: var(--text-primary); font-size: var(--font-size-xl); }
@keyframes pulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }

/* ===== Market Ticker ===== */
.market-ticker { margin-top: var(--s-5); padding-bottom: var(--s-5); }
.market-swiper { width: 100%; }
.market-swiper .swiper-slide { width: auto; }
.coin-card {
  background: rgba(255,255,255,.05);
  border-radius: var(--radius-lg);
  padding: var(--s-3) var(--s-4);
  min-width: 140px;
}
.coin-info { display: flex; flex-direction: column; gap: var(--s-1); }
.coin-symbol { font-size: var(--font-size-base); font-weight: 600; color: var(--text-primary); }
.coin-price { font-size: var(--font-size-md); font-weight: 700; }
.coin-change { font-size: var(--font-size-xs); }
.coin-price.up,.coin-change.up { color: var(--up); }
.coin-price.down,.coin-change.down { color: var(--down); }
</style>
